import React, { useEffect } from 'react';
import { MaxContext } from './context';
import { config } from './preference';
import { cachePageInstances, cacheSubPages, Logger, MaxPageRender, subPageSetterMapping } from '.';
import ReactDOM from 'react-dom';
import { MaxElement, MaxElementBuilder } from '@sm/max-common';

type AppProps = {
  page: MaxElement;
  pageCode: string;
  instanceId: string;
  instance: (ele: MaxElementBuilder, leaf: any, type: string) => React.FunctionComponentElement<any>;
  maxPageRender: MaxPageRender;
};
/**
 * 页面组件（父页面，子页面）
 */
function App(props: AppProps) {
  const { page, pageCode, instanceId, instance, maxPageRender } = props;

  useEffect(() => {
    // 没有缓存的说明没有加载过，需要执行 onReady
    if (!cachePageInstances.has(instanceId)) {
      if (config.isTopCache || page.props?.isCache) {
        cachePageInstances.set(instanceId, page);
      }
      page.onReady?.();
    }
    if (config.isAppSim || page.props?.isCache) {
      page.onShow?.();
    }

    // 通知 maxPageRender 当前页面渲染完成，继续渲染兄弟页面和子级页面
    maxPageRender.pageMountFinish(pageCode);

    return () => {
      if (config.isAppSim || page.props?.isCache) {
        page?.onHide?.();
      }
      // 没有缓存了，说明需要销毁
      if (!cachePageInstances.has(instanceId)) {
        page.onUnload?.();
      }
      // 通知 maxPageRender 当前页面卸载完成，继续卸载兄弟页面和父级页面
      maxPageRender.pageUnMountFinish(pageCode);
    };
  }, []);

  return (
    <MaxContext.Provider value={{ pageCode, instanceId }}>{instance(() => page, null, 'column')}</MaxContext.Provider>
  );
}

export const createPage =
  (maxPageRender: MaxPageRender) =>
  (
    element: (id?: string) => any,
    pageCode: string,
    parentPageCode: string,
    instance: (ele: () => any, leaf: any, type: string) => React.FunctionComponentElement<any>,
    externalId?: string,
  ) => {
    const instanceId = externalId ?? pageCode;

    let targetKey = '#max-app';
    if (parentPageCode) targetKey = `div#${parentPageCode}`;
    const target = document.querySelector(targetKey);

    if (!target) {
      Logger.warn(`can not find parent page ${parentPageCode}`);
      return;
    }

    if (parentPageCode) {
      const page = cachePageInstances.get(instanceId) ?? element(instanceId);
      // 子页面的渲染直接通知父页面下面的subPage容器进行渲染
      subPageSetterMapping.get(parentPageCode)?.(
        <App
          page={page}
          pageCode={pageCode}
          instanceId={instanceId}
          instance={instance}
          maxPageRender={maxPageRender}
        />,
      );
    } else {
      /**
       * 为什么没有用 createRoot(target).render 取代 ReactDOM.render？
       * - ReactDOM.render 在React18及后续版本会废弃，理应使用新API替换掉，但是这里暂时不能废弃，原因如下：
       * - ReactDOM.render 和 createRoot(target).render 默认都是在目标dom节点上重新挂载React产物，重新执行时也会执行挂载在当前根节点dom树内组件的生命周期，但差别是 ReactDOM.render 执行后能触发根dom之外的 React 的 componentWillUnmount，但是 createRoot(target).render 仅触发「根节点内 DOM 组件」的卸载生命周期 —— 这是 React 18 为新特性设计的 Break Change。
       * - antd 的弹窗、抽屉等浮层组件默认会挂载在 document.body 节点上，并且当对应的React实现中会在 componentWillUnmount 中执行组件dom节点的卸载操作。
       * - 使用 createRoot(target).render 重新挂载页面或者 createRoot(target).unmount() 卸载页面时，由于不会触发组件的 componentWillUnmount，所以就不能触发上述情况的弹窗等浮层组件的dom卸载操作。
       *
       * - 如何彻底解决？
       *  - 所有浮层类组件的默认挂载位置都放到页面的根dom上，也就是现在的 #max-app 节点上，这样在新版React中就能使用 createRoot 完全控制浮层类组件了。
       *  - 此举需要组件侧配合改造支撑组件挂载位置的自定义，至少组件侧需要显性消费 getContainer()
       */

      // 父页面使用 createRoot 渲染
      // createRoot(target).render(
      //   <App
      //     key={(Math.random() * 10000).toFixed(0)}
      //     page={page}
      //     pageCode={pageCode}
      //     instanceId={instanceId}
      //     instance={instance}
      //   />,
      // );

      const page = cachePageInstances.get(instanceId) ?? element(instanceId);

      ReactDOM.render(
        <App
          key={(Math.random() * 10000).toFixed(0)}
          page={page}
          pageCode={pageCode}
          instanceId={instanceId}
          instance={instance}
          maxPageRender={maxPageRender}
        />,
        target,
      );
    }

    cacheSubPages.set(pageCode, (instanceId: string) =>
      createPage(maxPageRender)(element, pageCode, parentPageCode, instance, instanceId),
    );
  };
